import { AiChatSessionsService } from '@/types/generated/services.gen'
import type { AIChatSession } from '@/types/generated/types.gen'
import { useUserStore } from '@/shared/stores/userStore'
import { onMounted, onUnmounted, ref, watch, type Ref } from 'vue'

/** Default popover session list fetch limit. */
export const DEFAULT_SESSION_LIST_LIMIT = 15
/** Default minimum delay between consecutive popover-driven refreshes. */
export const DEFAULT_SESSION_LIST_REFRESH_COOLDOWN_MS = 5_000
export const SESSION_OPTIMISTIC_REMOVED_EVENT = 'jupus:chat-session-optimistic-removed'

export interface UseChatSessionHistoryOptions {
  /**
   * Reactive popover open flag. When provided, sessions are refreshed on open and
   * any in-flight request is invalidated on close (generation counter bump).
   */
  popoverOpen?: Ref<boolean>
  /** Fetch limit forwarded to ``aiChatSessionsList``. */
  limit?: number
  /** Minimum delay between consecutive non-forced fetches. */
  cooldownMs?: number
}

/**
 * Shared session-history state for the chat history popover (fullscreen title +
 * side-panel header). Centralises the fetch-with-generation-counter + cooldown
 * pattern so both surfaces stay in sync and are both safe against:
 *
 * - Rapid open/close cycles generating a burst of concurrent ``aiChatSessionsList``
 *   calls (cooldown).
 * - Late responses mutating state after the popover closed or the user navigated
 *   away (generation counter).
 *
 * :param options: Behaviour knobs; see :class:`UseChatSessionHistoryOptions`.
 *
 * :return: Reactive session buckets plus ``fetchSessions`` / ``cancelInFlight`` for
 *     manual control (mount hooks, focus-back refreshes, etc.).
 */
export function useChatSessionHistory(options: UseChatSessionHistoryOptions = {}) {
  const {
    popoverOpen,
    limit = DEFAULT_SESSION_LIST_LIMIT,
    cooldownMs = DEFAULT_SESSION_LIST_REFRESH_COOLDOWN_MS
  } = options

  const userStore = useUserStore()

  const mySessions = ref<AIChatSession[]>([])
  const teamSessions = ref<AIChatSession[]>([])
  const loading = ref(false)
  const optimisticallyRemovedSessionUuids = ref<Set<string>>(new Set())

  let fetchGeneration = 0
  let lastFetchAt = 0

  /**
   * Bump the generation counter to invalidate any in-flight fetch. Safe to call
   * from ``onUnmounted`` / popover-close watchers.
   */
  const cancelInFlight = () => {
    fetchGeneration += 1
    loading.value = false
  }

  const hideOptimisticallyRemovedSession = (sessionUuid: string) => {
    if (!sessionUuid) return
    optimisticallyRemovedSessionUuids.value = new Set([
      ...optimisticallyRemovedSessionUuids.value,
      sessionUuid
    ])
    mySessions.value = mySessions.value.filter((session) => session.uuid !== sessionUuid)
    teamSessions.value = teamSessions.value.filter((session) => session.uuid !== sessionUuid)
  }

  const onSessionOptimisticallyRemoved = (event: Event) => {
    const customEvent = event as CustomEvent<{ sessionUuid?: string }>
    const sessionUuid = customEvent.detail?.sessionUuid
    if (!sessionUuid) return
    hideOptimisticallyRemovedSession(sessionUuid)
  }

  /**
   * Fetch the latest sessions and split them into my vs. team buckets. The default
   * call respects the cooldown; pass ``{ force: true }`` to bypass it (e.g. after
   * a rename or delete).
   *
   * :param opts: ``force`` skips the cooldown; otherwise the call is a no-op
   *     within ``cooldownMs`` of the last attempt.
   * :return: ``true`` when the fetch ran and updated state, ``false`` when it was
   *     skipped due to cooldown or invalidated by a newer call.
   */
  const fetchSessions = async (opts: { force?: boolean } = {}): Promise<boolean> => {
    if (!opts.force && lastFetchAt !== 0 && Date.now() - lastFetchAt < cooldownMs) {
      return false
    }
    const gen = ++fetchGeneration
    lastFetchAt = Date.now()
    loading.value = true
    try {
      const res = await AiChatSessionsService.aiChatSessionsList({
        limit,
        ordering: '-created_at'
      })
      if (gen !== fetchGeneration) return false
      const all = (res.results ?? []).filter(
        (session) => !optimisticallyRemovedSessionUuids.value.has(session.uuid)
      )
      const currentUuid = userStore.user?.uuid
      mySessions.value = all.filter((s) => s.started_by_user === currentUuid)
      teamSessions.value = all.filter((s) => s.started_by_user !== currentUuid)
      return true
    } catch (error) {
      if (gen !== fetchGeneration) return false
      // Reset the cooldown so the next open retries instead of sitting on a stale
      // empty state after a transient network blip.
      lastFetchAt = 0
      if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
        console.debug('[useChatSessionHistory] fetchSessions failed', error)
      }
      return false
    } finally {
      if (gen === fetchGeneration) {
        loading.value = false
      }
    }
  }

  onMounted(() => {
    if (typeof window === 'undefined') return
    window.addEventListener(SESSION_OPTIMISTIC_REMOVED_EVENT, onSessionOptimisticallyRemoved)
  })

  if (popoverOpen) {
    watch(popoverOpen, (isOpen) => {
      if (isOpen) {
        void fetchSessions()
      } else {
        cancelInFlight()
      }
    })
  }

  onUnmounted(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener(SESSION_OPTIMISTIC_REMOVED_EVENT, onSessionOptimisticallyRemoved)
    }
    cancelInFlight()
  })

  return {
    mySessions,
    teamSessions,
    loading,
    fetchSessions,
    cancelInFlight
  }
}
